import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { agentAPI } from "./agentAPI";

const initialState = {
  recommendations: [],
  dataSummary: null,
  dataGaps: null,
  chatMessages: [],
  tips: {}, // Keyed by module name
  loadingRecommendations: false,
  errorRecommendations: null,
  loadingChat: false,
  errorChat: null,
  loadingTips: {}, // Keyed by module name
  drawerOpen: false,
  progressStep: null,
  progressMessage: null,
  requestInProgress: false,
  agentEnabled: null,
};

export const fetchRecommendations = createAsyncThunk(
  "agent/fetchRecommendations",
  async (collectionName, { rejectWithValue }) => {
    try {
      const response = await agentAPI.getRecommendations(collectionName);
      return response.data; // Expected: { recommendations: [...], dataSummary, dataGaps }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to fetch recommendations");
    }
  }
);

export const dismissRecommendation = createAsyncThunk(
  "agent/dismissRecommendation",
  async ({ collectionName, recommendationId }, { rejectWithValue }) => {
    try {
      await agentAPI.sendFeedback(collectionName, recommendationId, "dismissed");
      return recommendationId;
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to dismiss recommendation");
    }
  }
);

export const fetchTips = createAsyncThunk(
  "agent/fetchTips",
  async ({ collectionName, moduleName }, { rejectWithValue }) => {
    try {
      const response = await agentAPI.getTips(collectionName, moduleName);
      return { moduleName, tips: response.data?.tips || [] };
    } catch (error) {
      return rejectWithValue({
        moduleName,
        error: error.response?.data?.message || error.message || "Failed to fetch tips",
      });
    }
  }
);

export const checkAgentEnabled = createAsyncThunk(
  "agent/checkEnabled",
  async (collectionName) => {
    try {
      const response = await agentAPI.checkEnabled(collectionName);
      return response.data.agentEnabled;
    } catch {
      return false;
    }
  }
);

// We can also have an async thunk for non-streaming asks
export const askQuestion = createAsyncThunk(
  "agent/askQuestion",
  async ({ collectionName, question, currentPage }, { rejectWithValue, dispatch }) => {
    try {
      const response = await agentAPI.ask(collectionName, question, currentPage);
      return response.data; // Expected: { answer, recommendations }
    } catch (error) {
      return rejectWithValue(error.response?.data?.message || error.message || "Failed to ask agent");
    }
  }
);

const agentSlice = createSlice({
  name: "agent",
  initialState,
  reducers: {
    addChatMessage: (state, action) => {
      state.chatMessages.push(action.payload); // payload: { id, sender: 'user' | 'agent', text, timestamp, followUpQuestions, isStreaming, progressStep, progressMessage }
    },
    updateLastAgentMessage: (state, action) => {
      const lastMsg = state.chatMessages[state.chatMessages.length - 1];
      if (lastMsg && lastMsg.sender === "agent") {
        if (typeof action.payload === "string") {
          lastMsg.text = action.payload;
        } else {
          Object.assign(lastMsg, action.payload);
        }
      }
    },
    setChatLoading: (state, action) => {
      state.loadingChat = action.payload;
    },
    clearChatHistory: (state) => {
      state.chatMessages = [];
    },
    setDrawerOpen: (state, action) => {
      state.drawerOpen = action.payload;
    },
    setProgress: (state, action) => {
      state.progressStep = action.payload.step;
      state.progressMessage = action.payload.message;
    },
    clearProgress: (state) => {
      state.progressStep = null;
      state.progressMessage = null;
    },
    setRequestInProgress: (state, action) => {
      state.requestInProgress = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch Recommendations
      .addCase(fetchRecommendations.pending, (state) => {
        state.loadingRecommendations = true;
        state.errorRecommendations = null;
        state.requestInProgress = true;
      })
      .addCase(fetchRecommendations.fulfilled, (state, action) => {
        state.loadingRecommendations = false;
        state.recommendations = action.payload?.recommendations || [];
        state.dataSummary = action.payload?.dataSummary || null;
        state.dataGaps = action.payload?.dataGaps || null;
        state.requestInProgress = false;
      })
      .addCase(fetchRecommendations.rejected, (state, action) => {
        state.loadingRecommendations = false;
        state.errorRecommendations = action.payload;
        state.requestInProgress = false;
      })

      // Dismiss Recommendation
      .addCase(dismissRecommendation.fulfilled, (state, action) => {
        state.recommendations = state.recommendations.filter(
          (rec) => rec.id !== action.payload
        );
      })

      // Fetch Tips
      .addCase(fetchTips.pending, (state, action) => {
        const { moduleName } = action.meta.arg;
        state.loadingTips[moduleName] = true;
      })
      .addCase(fetchTips.fulfilled, (state, action) => {
        const { moduleName, tips } = action.payload;
        state.loadingTips[moduleName] = false;
        state.tips[moduleName] = tips;
      })
      .addCase(fetchTips.rejected, (state, action) => {
        const moduleName = action.payload?.moduleName || action.meta.arg.moduleName;
        state.loadingTips[moduleName] = false;
        // Keep existing tips or set to empty array on failure
      })

      // Ask Question (fallback/non-streaming flow)
      .addCase(askQuestion.pending, (state) => {
        state.loadingChat = true;
        state.errorChat = null;
      })
      .addCase(askQuestion.fulfilled, (state, action) => {
        state.loadingChat = false;
        state.chatMessages.push({
          id: `msg_${Date.now()}`,
          sender: "agent",
          text: action.payload?.answer || "No response received.",
          timestamp: new Date().toLocaleTimeString(),
          followUpQuestions: action.payload?.followUpQuestions || [],
        });
        if (action.payload?.recommendations?.length > 0) {
          // Add new recommendations to the pool, ensuring uniqueness
          const existingIds = new Set(state.recommendations.map(r => r.id));
          const newRecs = (action.payload.recommendations || []).filter(r => !existingIds.has(r.id));
          state.recommendations = [...newRecs, ...state.recommendations];
        }
      })
      .addCase(askQuestion.rejected, (state, action) => {
        state.loadingChat = false;
        state.errorChat = action.payload;
        state.chatMessages.push({
          id: `msg_err_${Date.now()}`,
          sender: "agent",
          text: `Error: ${action.payload}`,
          timestamp: new Date().toLocaleTimeString(),
          isError: true,
        });
      })
      .addCase(checkAgentEnabled.fulfilled, (state, action) => {
        state.agentEnabled = action.payload;
      })
      .addCase(checkAgentEnabled.rejected, (state) => {
        state.agentEnabled = false;
      });
  },
});

export const { addChatMessage, updateLastAgentMessage, setChatLoading, clearChatHistory, setDrawerOpen, setProgress, clearProgress, setRequestInProgress } = agentSlice.actions;

export const selectRecommendations = (state) => state.agent.recommendations;
export const selectDataSummary = (state) => state.agent.dataSummary;
export const selectDataGaps = (state) => state.agent.dataGaps;
export const selectChatMessages = (state) => state.agent.chatMessages;
export const selectChatLoading = (state) => state.agent.loadingChat;
export const selectTipsByModule = (moduleName) => (state) => state.agent.tips[moduleName] || [];
export const selectTipsLoading = (moduleName) => (state) => !!state.agent.loadingTips[moduleName];
export const selectDrawerOpen = (state) => state.agent.drawerOpen;
export const selectProgressStep = (state) => state.agent.progressStep;
export const selectProgressMessage = (state) => state.agent.progressMessage;
export const selectRequestInProgress = (state) => state.agent.requestInProgress;
export const selectAgentEnabled = (state) => state.agent.agentEnabled;

export default agentSlice.reducer;
